import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { type ChatAppearance } from '@apps/web/components/phoebe_chat/chat_appearance';
import {
    ReferenceChip,
    ReferenceChipTray,
    ReferenceRemoveButton,
} from '@apps/web/components/phoebe_chat/reference_chip';
import { Button } from '@apps/web/components/ui/button';
import { UserAvatar } from '@apps/web/components/ui/user_avatar';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    ACTIVITY_HANDOFF_MAX,
    type ActivityHandoffItem,
    activityHandoffStore,
    buildActivityHandoffContextKey,
    useActivityHandoff,
} from '@apps/web/lib/activity_handoff_store';
import { useMode } from '@apps/web/lib/mode_store';

const SUMMARY_THRESHOLD = 4;
const MAX_STACK_AVATARS = 3;

function itemAvatarName(item: ActivityHandoffItem): string {
    return item.personLabel ?? item.title;
}

function ItemAvatarStack({
    items,
}: {
    readonly items: ReadonlyArray<ActivityHandoffItem>;
}) {
    const overflowCount =
        items.length > MAX_STACK_AVATARS
            ? items.length - (MAX_STACK_AVATARS - 1)
            : 0;
    const visibleItems =
        overflowCount > 0 ? items.slice(0, MAX_STACK_AVATARS - 1) : items;
    return (
        <div className="flex shrink-0 -space-x-1.5">
            {visibleItems.map((item) => (
                <UserAvatar
                    key={item.activityEventId}
                    displayName={itemAvatarName(item)}
                    colorKey={item.activityEventId}
                    className="ring-background size-6 ring-2"
                />
            ))}
            {overflowCount > 0 ? (
                <span className="bg-muted text-muted-foreground ring-background flex size-6 items-center justify-center rounded-full text-[10px] font-medium ring-2">
                    {`+${overflowCount}`}
                </span>
            ) : null}
        </div>
    );
}

/**
 * Activity-board cards attached to the next chat message, rendered above the
 * composer as removable reference rows (the same treatment as the calendar's
 * shift reference tray). The selection lives in `activityHandoffStore`; the
 * send path reads it for `activity_event_ids` and entity reference tokens.
 */
export function ActivityReferenceTray({
    appearance = 'default',
}: {
    readonly appearance?: ChatAppearance;
}) {
    const { contextKey, orderedItems } = useActivityHandoff();
    const organizationId = useOrganizationId();
    const mode = useMode() ?? 'live';
    const [isExpanded, setIsExpanded] = useState(false);
    const isEmpty = orderedItems.length === 0;
    useEffect(() => {
        if (isEmpty) {
            setIsExpanded(false);
        }
    }, [isEmpty]);
    if (isEmpty) {
        return null;
    }
    // A selection made under another org or mode is stale here; hide it
    // without clearing so it reappears when the user switches back.
    if (
        contextKey !== null &&
        contextKey !== buildActivityHandoffContextKey(organizationId, mode)
    ) {
        return null;
    }

    if (orderedItems.length < SUMMARY_THRESHOLD) {
        return (
            <ReferenceChipTray
                appearance={appearance}
                testId="activity-reference-tray"
            >
                {orderedItems.map((item) => (
                    <ReferenceChip
                        key={item.activityEventId}
                        appearance={appearance}
                        leading={
                            <UserAvatar
                                displayName={itemAvatarName(item)}
                                colorKey={item.activityEventId}
                                className="size-5 shrink-0"
                            />
                        }
                        hideLeadingInV3={true}
                        removeLabel={`Remove ${item.title} from message`}
                        removeTestId="activity-reference-remove"
                        rowTestId="activity-reference-row"
                        onRemove={() =>
                            activityHandoffStore.removeItem(
                                item.activityEventId,
                            )
                        }
                    >
                        <span className="min-w-0 flex-1 truncate text-xs leading-4">
                            <span className="font-medium">{item.title}</span>
                            {item.subtitle ? (
                                <span className="text-muted-foreground">
                                    {` · ${item.subtitle}`}
                                </span>
                            ) : null}
                        </span>
                    </ReferenceChip>
                ))}
            </ReferenceChipTray>
        );
    }

    const isAtCap = orderedItems.length >= ACTIVITY_HANDOFF_MAX;
    const summaryTitle = `${orderedItems.length} activity items`;
    const summarySubtitle = isAtCap
        ? `max ${ACTIVITY_HANDOFF_MAX}`
        : 'Attached to your next message';
    const ChevronIcon = isExpanded ? ChevronUp : ChevronDown;

    return (
        <div
            data-testid="activity-reference-tray"
            className="border-border bg-background mb-2 rounded-xl border"
        >
            <div
                data-testid="activity-reference-summary"
                className="flex items-center gap-2.5 px-2.5 py-2"
            >
                <ItemAvatarStack items={orderedItems} />
                <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={
                        isExpanded
                            ? 'Collapse attached activity items'
                            : 'Expand attached activity items'
                    }
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => setIsExpanded((previous) => !previous)}
                >
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                            {summaryTitle}
                        </span>
                        <span className="text-muted-foreground block truncate text-xs">
                            {summarySubtitle}
                        </span>
                    </span>
                    <ChevronIcon className="text-muted-foreground h-4 w-4 shrink-0" />
                </button>
                <button
                    type="button"
                    aria-label="Clear attached activity items"
                    data-testid="activity-reference-clear"
                    className="text-muted-foreground hover:text-foreground flex h-5 w-5 shrink-0 items-center justify-center rounded"
                    onClick={(event) => {
                        event.stopPropagation();
                        activityHandoffStore.clear();
                    }}
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
            {isExpanded ? (
                <div className="border-border border-t">
                    <div className="max-h-64 space-y-1 overflow-y-auto p-2">
                        {orderedItems.map((item) => (
                            <div
                                key={item.activityEventId}
                                data-testid="activity-reference-row"
                                className="bg-muted/40 hover:bg-muted/70 flex items-center gap-2 rounded-lg px-2 py-1"
                            >
                                <UserAvatar
                                    displayName={itemAvatarName(item)}
                                    colorKey={item.activityEventId}
                                    className="size-5 shrink-0"
                                />
                                <span className="min-w-0 flex-1 truncate text-xs">
                                    <span className="font-medium">
                                        {item.title}
                                    </span>
                                    {item.subtitle ? (
                                        <span className="text-muted-foreground">
                                            {` · ${item.subtitle}`}
                                        </span>
                                    ) : null}
                                </span>
                                <ReferenceRemoveButton
                                    appearance="default"
                                    label={`Remove ${item.title} from message`}
                                    testId="activity-reference-remove"
                                    onClick={() =>
                                        activityHandoffStore.removeItem(
                                            item.activityEventId,
                                        )
                                    }
                                />
                            </div>
                        ))}
                    </div>
                    <div className="border-border border-t p-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => activityHandoffStore.clear()}
                        >
                            Clear all
                        </Button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
